import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import { emailAPI, caseAPI, clientAPI } from '../utils/api';
import { toast } from 'sonner';
import { 
  Mail, 
  Send, 
  Inbox, 
  FileText,
  Trash2, 
  Eye, 
  X,
  Loader2,
  Search,
  Edit3,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Paperclip,
  RefreshCw
} from 'lucide-react';

export default function EmailModule() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFolder, setActiveFolder] = useState(searchParams.get('folder') || 'sent');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Compose form state
  const [composeForm, setComposeForm] = useState({
    to_email: searchParams.get('to') || '',
    subject: '',
    body: '',
    case_id: searchParams.get('case_id') || '',
    client_id: '',
    attachments: []
  });
  const [attachmentFiles, setAttachmentFiles] = useState([]);

  const folders = [
    { id: 'inbox', label: 'Inbox', icon: Inbox },
    { id: 'sent', label: 'Sent', icon: Send },
    { id: 'drafts', label: 'Drafts', icon: FileText },
  ];

  useEffect(() => {
    fetchEmails();
    fetchClients();
    fetchUnreadCount();
  }, [activeFolder]);

  useEffect(() => {
    // Open compose modal if 'compose' param is set
    if (searchParams.get('compose') === 'true') {
      setShowComposeModal(true);
    }
  }, [searchParams]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const res = await emailAPI.getEmails(activeFolder);
      setEmails(res.data);
    } catch (error) {
      toast.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await clientAPI.getClients();
      setClients(res.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await emailAPI.getUnreadCount();
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Failed to get unread count');
    }
  };

  const handleSendEmail = async () => {
    if (!composeForm.to_email || !composeForm.subject || !composeForm.body) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      const emailData = {
        ...composeForm,
        attachments: attachmentFiles
      };
      await emailAPI.sendEmail(emailData);
      toast.success('Email sent successfully');
      setShowComposeModal(false);
      setComposeForm({ to_email: '', subject: '', body: '', case_id: '', client_id: '', attachments: [] });
      setAttachmentFiles([]);
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleAddAttachment = (e) => {
    const files = Array.from(e.target.files);
    setAttachmentFiles(prev => [...prev, ...files]);
    e.target.value = ''; // Reset input
  };

  const handleRemoveAttachment = (index) => {
    setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveDraft = async () => {
    try {
      await emailAPI.saveDraft(composeForm);
      toast.success('Draft saved');
      setShowComposeModal(false);
      if (activeFolder === 'drafts') {
        fetchEmails();
      }
    } catch (error) {
      toast.error('Failed to save draft');
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft?')) return;
    
    try {
      await emailAPI.deleteDraft(draftId);
      toast.success('Draft deleted');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const handleViewEmail = async (emailId) => {
    try {
      const res = await emailAPI.getEmailDetail(emailId);
      setSelectedEmail(res.data);
      setShowViewModal(true);
      fetchUnreadCount();
    } catch (error) {
      toast.error('Failed to load email');
    }
  };

  const handleReply = (email) => {
    setComposeForm({
      to_email: email.from_email !== 'noreply@immiwaysmigration.in' ? email.from_email : email.to_email,
      subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
      body: `\n\n--- Original Message ---\nFrom: ${email.from_name}\nDate: ${new Date(email.sent_at).toLocaleString()}\n\n${email.body}`,
      case_id: email.case_id || '',
      client_id: email.client_id || ''
    });
    setShowViewModal(false);
    setShowComposeModal(true);
  };

  const filteredEmails = emails.filter(email => {
    const query = searchQuery.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(query) ||
      email.to_email?.toLowerCase().includes(query) ||
      email.from_email?.toLowerCase().includes(query) ||
      email.body?.toLowerCase().includes(query)
    );
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle size={14} className="text-green-500" />;
      case 'failed':
        return <AlertCircle size={14} className="text-red-500" />;
      default:
        return <Clock size={14} className="text-amber-500" />;
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content bg-slate-50">
        <TopHeader 
          title="Email" 
          subtitle="Send and manage emails within the platform"
        >
          <button
            onClick={() => setShowComposeModal(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-all flex items-center gap-2"
          >
            <Edit3 size={18} />
            Compose
          </button>
        </TopHeader>

        <div className="flex gap-6">
          {/* Sidebar - Folders */}
          <div className="w-56 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => {
                    setActiveFolder(folder.id);
                    setSearchParams({ folder: folder.id });
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 mb-1 ${
                    activeFolder === folder.id
                      ? 'bg-teal-50 text-teal-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <folder.icon size={18} />
                  {folder.label}
                  {folder.id === 'inbox' && unreadCount > 0 && (
                    <span className="ml-auto px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search & Refresh */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search emails..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                />
              </div>
              <button
                onClick={fetchEmails}
                className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-white border border-slate-200 rounded-lg"
              >
                <RefreshCw size={18} />
              </button>
            </div>

            {/* Email List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-teal-500" size={32} />
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
                <Mail size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Emails Found</h3>
                <p className="text-slate-500 mb-4">
                  {activeFolder === 'drafts' 
                    ? 'No saved drafts.'
                    : activeFolder === 'inbox'
                    ? 'No incoming emails yet.'
                    : 'No sent emails yet.'}
                </p>
                <button
                  onClick={() => setShowComposeModal(true)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"
                >
                  Compose Email
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {filteredEmails.map((email, index) => (
                  <div
                    key={email.id}
                    className={`flex items-center gap-4 px-5 py-4 hover:bg-slate-50 cursor-pointer transition-all ${
                      index !== 0 ? 'border-t border-slate-100' : ''
                    } ${!email.is_read && email.is_incoming ? 'bg-teal-50/50' : ''}`}
                    onClick={() => activeFolder === 'drafts' 
                      ? setComposeForm({ ...email, to_email: email.to_email || '' }) && setShowComposeModal(true)
                      : handleViewEmail(email.id)
                    }
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      email.is_incoming ? 'bg-blue-100 text-blue-600' : 'bg-teal-100 text-teal-600'
                    }`}>
                      <User size={18} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-medium ${!email.is_read && email.is_incoming ? 'text-slate-900' : 'text-slate-700'}`}>
                          {email.is_incoming ? email.from_name : email.to_name || email.to_email}
                        </span>
                        {email.case_id && (
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">
                            Case linked
                          </span>
                        )}
                        {getStatusIcon(email.status)}
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`truncate ${!email.is_read && email.is_incoming ? 'font-medium text-slate-900' : 'text-slate-700'}`}>
                          {email.subject || '(No subject)'}
                        </span>
                        <span className="text-slate-400 text-sm truncate flex-shrink">
                          - {email.body?.substring(0, 50)}...
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-slate-500 flex-shrink-0">
                      {email.attachments?.length > 0 && <Paperclip size={14} />}
                      <span>{new Date(email.created_at).toLocaleDateString()}</span>
                      {activeFolder === 'drafts' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteDraft(email.id); }}
                          className="p-1 text-slate-400 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Compose Modal */}
        {showComposeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[85vh] flex flex-col">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">New Email</h2>
                <button onClick={() => setShowComposeModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">To *</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={composeForm.to_email}
                      onChange={(e) => setComposeForm({ ...composeForm, to_email: e.target.value })}
                      placeholder="recipient@email.com"
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                    />
                    <select
                      value={composeForm.client_id}
                      onChange={(e) => {
                        const client = clients.find(c => c.id === e.target.value);
                        setComposeForm({ 
                          ...composeForm, 
                          client_id: e.target.value,
                          to_email: client?.email || composeForm.to_email
                        });
                      }}
                      className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                    >
                      <option value="">Select Client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.full_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={composeForm.subject}
                    onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                    placeholder="Email subject"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Message *</label>
                  <textarea
                    value={composeForm.body}
                    onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                    rows={10}
                    placeholder="Write your message..."
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none resize-none"
                  />
                </div>

                {/* Attachments Section */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Attachments</label>
                  <div className="border border-dashed border-slate-300 rounded-lg p-4">
                    <input
                      type="file"
                      multiple
                      onChange={handleAddAttachment}
                      className="hidden"
                      id="email-attachments"
                    />
                    <label 
                      htmlFor="email-attachments"
                      className="flex items-center justify-center gap-2 cursor-pointer text-sm text-slate-500 hover:text-teal-600"
                    >
                      <Paperclip size={18} />
                      <span>Click to add attachments</span>
                    </label>
                    {attachmentFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachmentFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg">
                            <div className="flex items-center gap-2 text-sm">
                              <Paperclip size={14} className="text-slate-400" />
                              <span className="text-slate-700 truncate max-w-[200px]">{file.name}</span>
                              <span className="text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-5 border-t border-slate-200 flex items-center justify-between">
                <button
                  onClick={handleSaveDraft}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg flex items-center gap-2"
                >
                  <FileText size={18} />
                  Save Draft
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowComposeModal(false)}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sending}
                    className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Email Modal */}
        {showViewModal && selectedEmail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[85vh] flex flex-col">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedEmail.subject}</h2>
                  <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                    <span>{selectedEmail.is_incoming ? 'From' : 'To'}: {selectedEmail.is_incoming ? selectedEmail.from_email : selectedEmail.to_email}</span>
                    <span>•</span>
                    <span>{new Date(selectedEmail.sent_at || selectedEmail.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-6">
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-slate-200">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedEmail.is_incoming ? 'bg-blue-100 text-blue-600' : 'bg-teal-100 text-teal-600'
                  }`}>
                    <User size={24} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {selectedEmail.is_incoming ? selectedEmail.from_name : selectedEmail.to_name || selectedEmail.to_email}
                    </p>
                    <p className="text-sm text-slate-500">
                      {selectedEmail.is_incoming ? selectedEmail.from_email : selectedEmail.to_email}
                    </p>
                  </div>
                  {selectedEmail.case_id && (
                    <span className="ml-auto text-xs px-3 py-1 bg-teal-100 text-teal-700 rounded-full">
                      Linked to Case
                    </span>
                  )}
                </div>

                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                    {selectedEmail.body}
                  </div>
                </div>

                {selectedEmail.attachments?.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Attachments</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmail.attachments.map((att, idx) => (
                        <a
                          key={idx}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-700 hover:bg-slate-200"
                        >
                          <Paperclip size={14} />
                          {att.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Close
                </button>
                <button
                  onClick={() => handleReply(selectedEmail)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                >
                  <Send size={18} />
                  Reply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
